import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Heart, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSession } from "@/lib/auth-client";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { api } from "$convex/convexApi";


function resolveImageUrl(product: any): string | null {
  if (!product) return null;
  if (product.imageMetadata?.url) return product.imageMetadata.url;
  if (product.image_metadata?.url) return product.image_metadata.url;
  if (typeof product.imageMetadata === "string") return product.imageMetadata;
  return null;
}

export default function WishlistPage() {
  const navigate = useNavigate();
  const { data: session, isPending: isSessionPending } = useSession();
  const isAuthenticated = Boolean(session?.user);

  const favorites = useQuery(
    api.wishlists.getUserFavorites,
    isAuthenticated ? {} : "skip",
  ) as any[] | undefined;

  const removeFavorite = useMutation(api.wishlists.removeFavorite);
  const [removingProductId, setRemovingProductId] = React.useState<string | null>(null);

  const isLoading = isSessionPending || (isAuthenticated && favorites === undefined);
  const favoriteList = favorites ?? [];

  const handleViewProduct = React.useCallback(
    (product: any) => {
      const productSlug = product?.slug;
      const productId = product?._id;
      if (productSlug) {
        navigate(`/product/${productSlug}`);
        return;
      }
      if (productId) {
        navigate(`/product/${productId}`);
      }
    },
    [navigate],
  );

  const handleRemoveFavorite = React.useCallback(
    async (productId: string) => {
      try {
        setRemovingProductId(productId);
        await removeFavorite({ productId: productId as any });
      } catch (error) {
        toast.error(
          toUserFacingErrorMessage(error, "Couldn't remove from wishlist. Please try again."),
        );
      } finally {
        setRemovingProductId(null);
      }
    },
    [removeFavorite],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Wishlist</h1>
          <p className="text-muted-foreground mt-1">
            {isAuthenticated ? `Your favorite products (${favoriteList.length})` : "Please log in to view your wishlist"}
          </p>
        </div>

        {isAuthenticated && favoriteList.length > 0 ? (
          <Button onClick={() => navigate("/offerlist")} className="gap-2">
            <ShoppingCart className="h-4 w-4" />
            Continue Shopping
          </Button>
        ) : null}
      </div>

      {!isAuthenticated ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Please log in to view and manage your wishlist.</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index}>
              <CardContent className="space-y-3 pt-6">
                <div className="aspect-square w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : favoriteList.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4 text-center">
              <Heart className="text-muted-foreground mx-auto h-12 w-12" />
              <div>
                <h3 className="text-lg font-semibold">Your wishlist is empty</h3>
                <p className="text-muted-foreground">Start adding products you love to your wishlist.</p>
              </div>
              <Button onClick={() => navigate("/offerlist")} className="gap-2">
                <ShoppingCart className="h-4 w-4" />
                Browse Products
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {favoriteList.map((favorite) => {
            const product = favorite.product;
            const productName = product?.name || "Unknown Product";
            const imageUrl = resolveImageUrl(product);
            const isRemoving = removingProductId === favorite.productId;

            return (
              <Card
                key={favorite._id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => handleViewProduct(product)}
              >
                <CardContent className="pt-6">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="line-clamp-2 text-base font-semibold leading-tight">{productName}</h3>
                      {product?.origin ? (
                        <p className="text-muted-foreground text-sm">{product.origin.name || product.origin}</p>
                      ) : null}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={isRemoving}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleRemoveFavorite(favorite.productId);
                      }}
                    >
                      <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                    </Button>
                  </div>

                  {imageUrl ? (
                    <div className="mb-3 aspect-square w-full overflow-hidden rounded-md bg-muted">
                      <img src={imageUrl} alt={productName} className="h-full w-full object-cover" />
                    </div>
                  ) : (
                    <div className="mb-3 aspect-square rounded-md bg-muted" />
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      Added {new Date(favorite.createdAt).toLocaleDateString()}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleViewProduct(product);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
